import {Box, Card, CardContent, Divider, Typography} from "@mui/material";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import {Colors, STATUS_STYLES} from "../../Constants/Colors.js";
import {ComponentConfig} from "../../Config/ComponentConfig.js";
import PrimaryButton from "../Common/PrimaryButton.tsx";
import React from "react";
import {Appointment} from "../../Types/index.ts";
import moment from "moment";
import {useNavigate} from "react-router-dom";
import {WebRoutes} from "../../Routes/WebRoutes.js";
import StatusTag from "./AppointmentStatusTag.tsx";
import {Constants} from "../../Constants/Constants.js";
import Api from "../../Interceptor/Api/index.ts";
import {toast} from "react-toastify";

interface AppointmentDetailCardProps {
    data: Appointment;
}

export const AppointmentDetailCard = ({data}: AppointmentDetailCardProps) => {
    const navigate = useNavigate();
    const formattedDate = moment(data?.appointment_start_time).format(
        "DD MMM, YYYY"
    );
    const formattedTime = moment(data?.appointment_start_time).format("hh:mm A");

    const complains = data?.appointment_complains
        ?.map((complain) => complain?.description)
        .join(", ");

    const teamNameByAppointmentStatus = {
        completed: "PX",
        upcoming: "CT",
        ongoing: "CT",
    };

    const handleFollowUp = async () => {
        try {
            const response = await Api.appointment.bookFollowUpAppointment(data?.id);
            localStorage.setItem("savedAppointment", JSON.stringify({
                ...response,
                appointment_start_time: null,
                created_at: new Date()
            }))
            navigate(`/book-doctor/step-3?appId=${response.appointment_id}`);
        } catch (error) {
            toast.error(error?.message);
        }
    }

    return (
        <Card
            sx={{
                width: "100%",
                margin: "auto",
                borderRadius: ComponentConfig.CardBorderRadius,
                boxShadow: 0,
                backgroundColor: Colors.appointmentCardBgColor,
            }}
        >
            <CardContent
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    alignItems: "flex-start",
                    justifyContent: "center",
                    padding: ComponentConfig.cardInnerPadding,
                }}
            >
                <Box
                    width="100%"
                    display="flex"
                    justifyContent="space-between"
                    alignItems="flex-start"
                >
                    {/* Patient Info */}
                    <Box display="flex" flexDirection="column" alignItems="flex-start">
                        <Typography
                            color={Colors.primaryTextColor}
                            className="subtitle-bold"
                            variant="subtitleBold"
                        >
                            {data?.patient?.user_full_name}
                        </Typography>
                        <Typography
                            color={Colors.primaryTextColor}
                            className="body-regular"
                            variant="bodyRegular"
                        >
                            {data?.patient?.age}, {data?.patient?.gender}
                        </Typography>
                    </Box>
                    {/* Status Tag */}
                    <StatusTag status={data?.status}/>
                </Box>

                {/* Doctor Info */}
                <Typography className="small-bold" variant="smallBold">
                    Dr. {data?.doctor?.user_full_name}
                </Typography>

                {complains && (
                    <Box display="flex" flexDirection="column" alignItems="flex-start">
                        <Typography
                            color={Colors.primaryTextColor}
                            className="small-regular"
                            variant="smallRegular"
                        >
                            Health Concerns
                        </Typography>
                        <Box
                            bgcolor="#D8D8D8"
                            borderRadius="10px"
                            paddingX={2}
                            paddingY={0.5}
                        >
                            <Typography
                                color={Colors.primaryTextColor}
                                className="body-regular"
                                variant="bodyRegular"
                            >
                                {complains}
                            </Typography>
                        </Box>
                    </Box>
                )}

                {/* Date and Time */}
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Box display="flex" alignItems="center">
                        <CalendarTodayIcon
                            sx={{mr: 0.5, width: ComponentConfig.defaultIconSize}}
                        />
                        <Typography className="small-regular" variant="smallRegular">
                            {formattedDate}
                        </Typography>
                    </Box>

                    <Divider
                        orientation="vertical"
                        color={Colors.grey}
                        flexItem
                        sx={{mx: 1}}
                    />

                    <Box display="flex" alignItems="center">
                        <AccessTimeIcon
                            sx={{mr: 0.5, width: ComponentConfig.defaultIconSize}}
                        />
                        <Typography className="small-regular" variant="smallRegular">
                            {formattedTime}
                        </Typography>
                    </Box>
                </Box>

                {data?.status === Constants.AppointmentStatus.COMPLETED && (
                    <Box display={"flex"} gap={1} flex={1}>
                        <PrimaryButton
                            variant={"outlined"}
                            text={"Book follow-up appointment"}
                            onTap={handleFollowUp}
                        />
                        <PrimaryButton
                            variant={"outlined"}
                            text={"Request follow-up call with Doctor"}
                            onTap={() => {
                                navigate(WebRoutes.REQUEST_CALL, {
                                    state: {
                                        appointmentId: data?.id,
                                        teamName: "Doctor",
                                        customerId: data?.patient?.id,
                                    },
                                });
                            }}
                        />
                    </Box>
                )}
                {data?.status === Constants.AppointmentStatus.COMPLETED ? (
                    <PrimaryButton
                        variant={"outlined"}
                        text={"Need help? Request a Call Back"}
                        onTap={() => {
                            navigate(WebRoutes.REQUEST_CALL, {
                                state: {
                                    appointmentId: data?.id,
                                    teamName: teamNameByAppointmentStatus[data?.status],
                                    customerId: data?.patient?.id,
                                },
                            });
                        }}
                    />
                ) : data?.status === Constants.AppointmentStatus.ONGOING ||
                data?.status === Constants.AppointmentStatus.UPCOMING ? (
                    <PrimaryButton
                        variant={"outlined"}
                        text={"Need help? Request a Call Back"}
                        backgroundColor={STATUS_STYLES[data?.status].tagColor}
                        borderColor={STATUS_STYLES[data?.status].textColor}
                        onTap={() => {
                            navigate(WebRoutes.REQUEST_CALL, {
                                state: {
                                    appointmentId: data?.id,
                                    teamName: teamNameByAppointmentStatus[data?.status],
                                    customerId: data?.patient?.id,
                                },
                            });
                        }}
                    />
                ) : data?.status === Constants.AppointmentStatus.CANCELLED ? (
                    <PrimaryButton
                        variant={"outlined"}
                        text={"Reschedule appointment"}
                        onTap={() => {
                            navigate(WebRoutes.REQUEST_CALL, {
                                state: {
                                    appointmentId: data?.id,
                                    teamName: teamNameByAppointmentStatus[data?.status],
                                    customerId: data?.patient?.id,
                                },
                            });
                        }}
                    />
                ) : null}
            </CardContent>
        </Card>
    );
};
